import { memo, useEffect, useRef } from 'react'

import Flyout from 'common-components/Flyout'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { MENTION_EDITOR_CLASS } from '../consts'
import * as Styled from './styled'
import { IProps } from './types'

/**
 * Lists the reference images for @mention insertion. Purely presentational —
 * all trigger/caret state lives in PromptEditor.
 *
 * Rendered through the repo's portal Flyout (anchored to a zero-size element
 * placed at the caret), so the sub-sidebar's scroll containers cannot clip it.
 *
 * Every listed option is selectable: the owning tab omits images that cannot be
 * referenced, so `imageNumber` may have gaps — see IMentionPickerOption.
 */
const MentionPicker = ({
  isOpen,
  anchorEl,
  options,
  highlightedIndex,
  onHighlight,
  onSelect,
  onClose
}: IProps) => {
  const listRef = useRef<HTMLDivElement | null>(null)

  // Keep the keyboard-highlighted row visible inside the scrollable list.
  useEffect(() => {
    const item = listRef.current?.children[highlightedIndex]

    if (item instanceof HTMLElement) {
      item.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  return (
    <Flyout
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      // Class names, not selectors: useOnClickOutside walks the parent chain.
      // Clicks inside the editor (including on a chip) must reach the editor's
      // own handlers — caret moves decide whether the picker stays open.
      ignoredSelectors={[MENTION_EDITOR_CLASS]}
      options={{ position: 'bottom', alignmentX: 'left', extraOffset: 4 }}
    >
      <Styled.OptionsList ref={listRef} data-testid="mention-picker">
        {options.map((option, index) => (
          <Styled.Option
            key={option.id}
            $isHighlighted={index === highlightedIndex}
            // preventDefault keeps the editor focused, so the caret (and the
            // blur-close behavior) survive the click.
            onMouseDown={event => event.preventDefault()}
            onMouseEnter={() => onHighlight(index)}
            onClick={() => onSelect(option.imageNumber)}
            data-testid={`mention-picker-item-${option.imageNumber}`}
          >
            {!!option.thumbnailUrl && (
              <Styled.Thumbnail src={option.thumbnailUrl} alt="" />
            )}
            <span>
              {replaceTranslationVariables(translate(T.MENTION_IMAGE_LABEL), [
                String(option.imageNumber)
              ])}
            </span>
          </Styled.Option>
        ))}
      </Styled.OptionsList>
    </Flyout>
  )
}

export default memo(MentionPicker)
